import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import SEO from "../utils/SEO";
import SubscriptionComponent from "../utils/SubscriptionComponent";
import { trackCTAClick } from "../../utils/analytics";
import { REVIEWER, EDITORIAL_PATH, reviewerSchema } from "../../config/eeat";
import { herbSlug } from "../../utils/herbSlug";
import {
  fetchConditionPage,
  getSourceRefIndex,
} from "../../services/knowledgeGraph";
import "../../styles/Article.css";
import "../../styles/Conditions.css";

const BASE_URL = "https://joinsarv.com";
const HERB_LIMIT = 20;

// Duke indexes some entries as "Cancer, tongue" / "Cramp, bile duct". Reading
// order for a page title wants the qualifier first. Purely presentational — the
// stored label is what schema.org and the citations refer to.
const readableLabel = (label) => {
  const s = String(label || "").trim();
  const m = s.match(/^([^,]+),\s*(.+)$/);
  if (!m) return s;
  const [, head, qualifier] = m;
  return `${qualifier.charAt(0).toUpperCase()}${qualifier.slice(
    1
  )} ${head.toLowerCase()}`;
};

// Every claim on this page renders its grade in words plus how many sources
// stand behind it. This component is the single place that rule is satisfied —
// if you render a claim without it, that's the bug the brief warns about.
const EvidenceBadge = ({ grade, meta, refCount }) => (
  <span className={`ev-badge ev-badge--${grade || "unknown"}`}>
    <span className="ev-badge__grade">{meta.label}</span>
    {typeof refCount === "number" && (
      <span className="ev-badge__refs">
        {refCount} {refCount === 1 ? "source" : "sources"}
      </span>
    )}
  </span>
);

// Citation codes resolved to short-form references; falls back to the raw code
// so a failed lookup still shows provenance rather than nothing.
const Citations = ({ codes, refIndex }) => {
  if (!codes || !codes.length) return null;
  return (
    <span className="condition-page__cites">
      {codes.map((c, i) => (
        <span
          key={c}
          className="condition-page__cite"
          title={refIndex.get(c) || c}
        >
          {refIndex.get(c) || c}
          {i < codes.length - 1 ? "; " : ""}
        </span>
      ))}
    </span>
  );
};

const ConditionPage = () => {
  const { slug } = useParams();
  const [status, setStatus] = useState("loading");
  const [condition, setCondition] = useState(null);
  const [herbs, setHerbs] = useState([]);
  const [actions, setActions] = useState([]);
  const [mix, setMix] = useState(null);
  const [formula, setFormula] = useState([]);
  const [monographs, setMonographs] = useState(new Map());
  const [refIndex, setRefIndex] = useState(new Map());

  useEffect(() => {
    let active = true;
    (async () => {
      setStatus("loading");
      try {
        // One round trip for the whole page (see fetchConditionPage). The
        // citation index is the only companion call: it is identical on every
        // condition page and memoised for the session, so it is fetched
        // alongside rather than folded into the payload.
        const [page, refs] = await Promise.all([
          fetchConditionPage(slug, { herbLimit: HERB_LIMIT }),
          getSourceRefIndex(),
        ]);
        if (!active) return;
        if (!page) {
          setStatus("notfound");
          return;
        }
        setCondition(page.condition);
        setHerbs(page.herbs);
        setActions(page.actions);
        setMix(page.mix);
        setFormula(page.formula);
        setMonographs(page.monographs);
        setRefIndex(refs);
        setStatus(page.herbs.length ? "ready" : "empty");
      } catch (e) {
        console.error("Error loading condition:", e);
        if (active) setStatus("error");
      }
    })();
    return () => {
      active = false;
    };
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="article-page condition-page">
        <p className="article-page__p">Loading…</p>
      </div>
    );
  }

  if (status === "notfound" || status === "empty") {
    return (
      <div className="article-page condition-page">
        <SEO
          title="Condition not found | Sarv"
          description="This condition page is not available."
          noIndex
        />
        <h1 className="article-page__title">Not found</h1>
        <p className="article-page__p">
          We don't have an evidence summary for this condition.{" "}
          <Link to="/conditions">Browse all conditions →</Link>
        </p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="article-page condition-page">
        <SEO
          title="Something went wrong | Sarv"
          description="Please try again."
          noIndex
        />
        <h1 className="article-page__title">Something went wrong</h1>
        <p className="article-page__p">
          We couldn't load this page. Please refresh, or{" "}
          <Link to="/conditions">browse all conditions →</Link>
        </p>
      </div>
    );
  }

  const name = readableLabel(condition.label);
  const url = `${BASE_URL}/conditions/${condition.slug}`;
  const topThree = herbs.slice(0, 3).map((h) => h.name);
  const description =
    `${
      herbs.length
    } herbs are traditionally or clinically associated with ${name.toLowerCase()}` +
    `${topThree.length ? `, including ${topThree.join(", ")}` : ""}. ` +
    `Each one is shown with its evidence grade and citations.`;

  // Data-driven FAQ — answerable strictly from the graph, no authored claims.
  const faq = [];
  if (topThree.length) {
    faq.push({
      q: `Which herbs are used for ${name.toLowerCase()}?`,
      a: `${topThree.join(
        ", "
      )} rank highest by evidence strength and number of supporting sources. Every herb listed on this page shows the grade of evidence behind it, from traditional use through to clinical trials.`,
    });
  }
  if (mix && mix.total) {
    faq.push({
      q: `Is there clinical evidence for herbs for ${name.toLowerCase()}?`,
      a:
        mix.clinical > 0
          ? `${mix.clinical} of ${
              mix.total
            } herbs listed here have human clinical data behind them; the remaining ${
              mix.total - mix.clinical
            } rest on laboratory, animal or traditional evidence only.`
          : `None of the ${mix.total} herbs listed here have human clinical data. They rest on laboratory, animal or traditional evidence, which is why each entry is graded.`,
    });
  }
  if (actions.length) {
    faq.push({
      q: `How do herbs act on ${name.toLowerCase()}?`,
      a: `Herbs used for this condition are most distinctively ${actions
        .slice(0, 3)
        .map((a) => a.label.toLowerCase())
        .join(
          ", "
        )} — these actions appear far more often among them than across herbs generally.`,
    });
  }

  const medSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    name: `${name}: Herbs and the Evidence Behind Them`,
    description,
    url,
    inLanguage: "en",
    about: { "@type": "MedicalCondition", name: condition.clinicalLabel },
    author: {
      "@type": "Organization",
      name: "Sarv Health Team",
      url: BASE_URL,
    },
    reviewedBy: reviewerSchema(),
    publisher: {
      "@type": "Organization",
      name: "Sarv",
      url: BASE_URL,
      logo: { "@type": "ImageObject", url: `${BASE_URL}/v.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: BASE_URL },
      {
        "@type": "ListItem",
        position: 2,
        name: "Conditions",
        item: `${BASE_URL}/conditions`,
      },
      { "@type": "ListItem", position: 3, name, item: url },
    ],
  };
  const faqSchema = faq.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  return (
    <>
      <article className="article-page condition-page">
        <SEO
          title={`${name}: Herbs & the Evidence Behind Them | Sarv`}
          description={description}
          canonicalPath={`/conditions/${condition.slug}`}
          keywords={`herbs for ${name.toLowerCase()}, ${name.toLowerCase()} herbal remedies, evidence based herbal medicine, ${topThree.join(
            ", "
          )}`}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(medSchema) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
        {faqSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
          />
        )}

        <nav className="article-page__breadcrumb" aria-label="breadcrumb">
          <Link to="/">Home</Link> › <Link to="/conditions">Conditions</Link> ›{" "}
          <span>{name}</span>
        </nav>

        <h1 className="article-page__title">Herbs for {name}</h1>
        {condition.clinicalLabel !== condition.label && (
          <p className="condition-page__sci">
            Indexed as <em>{condition.clinicalLabel}</em>
          </p>
        )}

        <p className="article-page__review">
          Reviewed by {REVIEWER.name} ·{" "}
          <Link to={EDITORIAL_PATH}>Our editorial process</Link>
        </p>

        {mix && mix.total > 0 && (
          <section className="condition-page__mix" aria-label="Evidence mix">
            <p className="article-page__lead">
              We found <strong>{mix.total}</strong> herbs associated with{" "}
              {name.toLowerCase()}.{" "}
              {mix.clinical > 0 ? (
                <>
                  <strong>{mix.clinical}</strong>{" "}
                  {mix.clinical === 1 ? "has" : "have"} human clinical data
                  behind {mix.clinical === 1 ? "it" : "them"}.{" "}
                </>
              ) : (
                <>None have been tested in humans. </>
              )}
              <strong>{mix.folkShare}%</strong> rest on traditional use with no
              science reviewed. Each row below shows which is which.
            </p>
            <ul className="condition-page__mixbar">
              {[
                [
                  "clinical_plant",
                  mix.counts.clinical_plant,
                  "Clinical trial, whole plant",
                ],
                [
                  "clinical_extract",
                  mix.counts.clinical_extract,
                  "Human data on extract",
                ],
                [
                  "preclinical",
                  mix.counts.preclinical,
                  "In vitro / animal only",
                ],
                ["folk", mix.counts.folk, "Traditional use only"],
              ]
                .filter(([, n]) => n > 0)
                .map(([grade, n, label]) => (
                  <li
                    key={grade}
                    className={`condition-page__mixitem ev-${grade}`}
                  >
                    <span className="condition-page__mixcount">{n}</span>
                    <span className="condition-page__mixlabel">{label}</span>
                  </li>
                ))}
            </ul>
          </section>
        )}

        {actions.length > 0 && (
          <section>
            <h2 className="article-page__h2">How these herbs act</h2>
            <p className="article-page__p">
              These are the pharmacological actions that most distinguish herbs
              used for {name.toLowerCase()} from herbs generally — mined from
              the graph, not authored. A lift of 5× means the action is five
              times more common here than its base rate.
            </p>
            <ul className="condition-page__actions">
              {actions.map((a) => (
                <li key={a.actionId} className="condition-page__action">
                  <span className="condition-page__action-label">
                    {a.label}
                  </span>
                  <span className="condition-page__action-lift">
                    {a.lift.toFixed(1)}×
                  </span>
                  <span className="condition-page__action-n">
                    {a.herbCount} herbs
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section>
          <h2 className="article-page__h2">Ranked herbs and their evidence</h2>
          <p className="article-page__p">
            Ranked by strength of evidence and the number of independent sources
            supporting the use. Herbs Duke rates as unsafe for
            self-administration are excluded from this list.
          </p>
          <div className="condition-page__tablewrap">
            <table className="condition-page__table">
              <caption className="sr-only">
                Herbs associated with {name}, with evidence grade, source count
                and citations
              </caption>
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Herb</th>
                  <th scope="col">Evidence</th>
                  <th scope="col">Sources</th>
                  <th scope="col">Safety</th>
                </tr>
              </thead>
              <tbody>
                {herbs.map((h) => {
                  const title = monographs.get(h.herbId);
                  return (
                    <tr key={h.herbId}>
                      <td>{h.displayRank}</td>
                      <td>
                        {title ? (
                          <Link to={`/herbs/${herbSlug({ title })}`}>
                            {h.name}
                          </Link>
                        ) : (
                          h.name
                        )}
                        <span className="condition-page__sciname">
                          {h.scientificName}
                        </span>
                      </td>
                      <td>
                        <EvidenceBadge
                          grade={h.evidence}
                          meta={h.evidenceMeta}
                        />
                      </td>
                      <td>
                        {h.refCount}
                        <Citations codes={h.refs} refIndex={refIndex} />
                      </td>
                      <td>
                        <span
                          className="condition-page__safety"
                          title="Duke safety rating"
                        >
                          {h.safetyScore || "—"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="condition-page__legend">
            Safety ratings are Duke's: <strong>+++</strong> safer than coffee ·{" "}
            <strong>++</strong> use with care · <strong>+</strong> use only
            under supervision.
          </p>
        </section>

        {formula.length > 0 && (
          <section>
            <h2 className="article-page__h2">A worked combination</h2>
            <p className="article-page__p">
              Built by covering the actions above with the fewest herbs — each
              step adds actions the previous ones don't have. This is an
              illustration of how herbalists reason about combinations, not a
              prescription.
            </p>
            <ol className="condition-page__formula">
              {formula.map((f) => (
                <li key={f.step} className="condition-page__formula-step">
                  <div className="condition-page__formula-head">
                    <strong>{f.name}</strong> <em>{f.scientificName}</em>
                    <EvidenceBadge grade={f.evidence} meta={f.evidenceMeta} />
                  </div>
                  {f.newActions.length > 0 && (
                    <div className="condition-page__formula-adds">
                      adds {f.newActions.join(", ").toLowerCase()}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}

        {faq.length > 0 && (
          <section className="article-page__faq">
            <h2 className="article-page__h2">Frequently asked questions</h2>
            {faq.map((f, i) => (
              <div key={i} className="article-page__faq-item">
                <h3 className="article-page__faq-q">{f.q}</h3>
                <p className="article-page__p">{f.a}</p>
              </div>
            ))}
          </section>
        )}

        <p className="article-page__disclaimer">
          This information is for general wellness and educational purposes only
          and is not medical advice. Traditional use is not evidence of
          effectiveness. Consult a qualified healthcare provider before using
          any herb — especially if you are pregnant, nursing, or taking
          medication.
        </p>

        <aside className="article-page__cta">
          <p>
            Want a natural routine matched to your body and goals?{" "}
            <Link
              to="/start?source=condition"
              onClick={() =>
                trackCTAClick("condition_start_trial", "condition-end")
              }
            >
              Get your personalized plan →
            </Link>
          </p>
        </aside>
      </article>

      <SubscriptionComponent />
    </>
  );
};

export default ConditionPage;
